import i18n from 'i18n';
import { notification } from 'features/notifications/toast/notification';
import { SignalingSocket } from 'services/signaling';
import { store as reduxStore } from 'store/store';
import { uploadFinished, uploadStarted } from 'features/whiteboard/whiteboardSlice';
import { convertToFileWithExtension, hasValidImageExtension } from 'utils/file';
import { addImageFileToUploader, createWbImageUploader } from 'utils/whiteboard/imageUploader';

type UploadedWhiteboardImage = {
  url: string;
  file: File;
};

type UploadWhiteboardImageOptions = {
  // Looked up by the caller rather than here: restoring uploads under the key of the board being
  // restored, which is not the board currently open.
  encryptionKey?: CryptoKey;
  // Set by a caller uploading a batch, where one systemic failure should report once. Left unset
  // for a person dropping images one at a time, who needs a toast for every attempt.
  toastId?: string;
};

export const uploadWhiteboardImage = async (
  file: File,
  { encryptionKey, toastId }: UploadWhiteboardImageOptions = {}
): Promise<UploadedWhiteboardImage | undefined> => {
  let tokenResponse: { token?: string } | undefined;

  try {
    tokenResponse = await SignalingSocket.sendAsync<{ token?: string }>({
      event: 'requestFileUpload',
    });
  } catch {
    return undefined;
  }

  if (!tokenResponse?.token) {
    return undefined;
  }

  const upload = hasValidImageExtension(file) ? file : convertToFileWithExtension(file);
  const uploader = createWbImageUploader(tokenResponse.token);

  reduxStore.dispatch(uploadStarted());

  try {
    try {
      await addImageFileToUploader(uploader, upload, encryptionKey);
    } catch {
      notification(i18n.t('notifications:wb_upload_file_invalid'), { toastId });
      return undefined;
    }

    // A dropped connection rejects here, and restoring a board uploads every image under one
    // `all()`: rethrowing would cost the whole board rather than the one image.
    const result = await uploader.upload().catch(() => undefined);

    if (result?.successful?.[0]) {
      return { url: result.successful[0].response?.body!.link as string, file: upload };
    }

    if (!result || result.failed?.[0]) {
      notification(i18n.t('notifications:wb_upload_failed'), { toastId });
    }

    return undefined;
  } finally {
    uploader.destroy();
    reduxStore.dispatch(uploadFinished());
  }
};
